import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthenticatedPrincipal, SessionEndReason } from '@autodialer/shared';

import { ApiError, authApi } from '../api/client';
import { useI18n } from '../i18n';

/**
 * Hält den angemeldeten Benutzer im Frontend.
 *
 * Wichtig: das ist nur die Anzeige. Ob jemand angemeldet ist und was er darf,
 * entscheidet ausschließlich der Server. Fällt eine Anfrage mit 401 zurück -
 * etwa weil die Sitzung durch eine neue Anmeldung abgelöst oder nach einer
 * Stunde beendet wurde -, führt die Oberfläche verständlich zum Login zurück.
 */

interface SessionValue {
  principal: AuthenticatedPrincipal | null;
  loading: boolean;
  /** Grund der letzten Abmeldung - für die Meldung auf der Loginseite. */
  endReason: SessionEndReason | null;
  login: (identifier: string, password: string) => Promise<void>;
  /** Anmeldung der Systemadministration. */
  loginPlatform: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Meldet eine abgelaufene Sitzung, wenn eine Anfrage mit 401 endet. */
  handleApiError: (error: unknown) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [principal, setPrincipal] = useState<AuthenticatedPrincipal | null>(null);
  const [loading, setLoading] = useState(true);
  const [endReason, setEndReason] = useState<SessionEndReason | null>(null);
  const { setLocale } = useI18n();

  const apply = useCallback(
    (next: AuthenticatedPrincipal | null) => {
      setPrincipal(next);
      if (next) {
        // Die am Konto gespeicherte Sprache gewinnt.
        setLocale(next.locale);
      }
    },
    [setLocale],
  );

  const refresh = useCallback(async () => {
    try {
      const response = await authApi.me();
      apply(response.principal);
      setEndReason(null);
    } catch (error) {
      apply(null);
      if (error instanceof ApiError && error.sessionEnded) {
        setEndReason(error.sessionEndReason ?? null);
      }
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Bewusst eigenstaendig und stabil.
   *
   * Komponenten benutzen `handleApiError` als Abhaengigkeit eines
   * `useEffect`. Steckte die Funktion im grossen `useMemo` unten, bekaeme sie
   * bei jeder Aenderung von `principal`, `loading` oder `endReason` eine neue
   * Identitaet - und jeder solche Effekt wuerde abgeraeumt und neu gestartet.
   * Beim Telefon hat genau das das Mikrofon zweimal in Folge angefordert und
   * den zweiten Versuch scheitern lassen (siehe telephony/microphone.ts).
   */
  const handleApiError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.sessionEnded) {
        apply(null);
        setEndReason(error.sessionEndReason ?? 'logout');
      }
    },
    [apply],
  );

  const value = useMemo<SessionValue>(
    () => ({
      principal,
      loading,
      endReason,
      login: async (identifier, password) => {
        const response = await authApi.login(identifier, password);
        apply(response.principal);
        setEndReason(null);
      },
      loginPlatform: async (email, password) => {
        const response = await authApi.platformLogin(email, password);
        apply(response.principal);
        setEndReason(null);
      },
      logout: async () => {
        await authApi.logout().catch(() => undefined);
        apply(null);
        setEndReason('logout');
      },
      refresh,
      handleApiError,
    }),
    [apply, endReason, handleApiError, loading, principal, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession muss innerhalb des SessionProvider verwendet werden.');
  }
  return value;
}
